import React from 'react';
import styled from 'styled-components';
import {Finger} from 'shared/components/Header/RoomPlanner/NavigationGuide/Finger';

interface GestureProps {
    index: number;
    motion: number;
    depth: boolean;
    touch: boolean;
    label: string;
    panVector?: [number, number];
    note?: string;
    fade?: number;
}

const getPanArrow = ([dx, dy]: [number, number]) => {
    const length = Math.hypot(dx, dy) || 1;
    const [unitX, unitY] = [dx / length, dy / length];
    const [tipX, tipY] = [160 + dx * 1.3, 51 + dy * 1.3];
    const barb = (side: number) => {
        const x = tipX - unitX * 7 - unitY * 5 * side;
        const y = tipY - unitY * 7 + unitX * 5 * side;

        return `${x.toFixed(1)} ${y.toFixed(1)}`;
    };
    const tip = `${tipX} ${tipY}`;

    return `M${160 - dx * 0.3} ${51 - dy * 0.3}L${tip} M${barb(1)}L${tip}L${barb(-1)}`;
};

export const Gesture = ({
    index,
    motion,
    depth,
    touch,
    label,
    panVector,
    note,
    fade = 1,
}: GestureProps) => {
    const reveal = {
        opacity: fade,
        transform: `translate(0 ${((1 - fade) * 6).toFixed(2)})`,
    };

    if (note) {
        return (
            <Svg viewBox="0 0 320 106" role="img" aria-label={label}>
                <g {...reveal}>
                    <text x={160} y={57} textAnchor="middle" className="note">
                        {note}
                    </text>
                </g>
            </Svg>
        );
    }

    const x = panVector
        ? panVector[0] * motion
        : index !== 2 && !depth
          ? motion * 43
          : 0;
    const y = panVector ? panVector[1] * motion : depth ? -motion * 17 : 0;

    return (
        <Svg viewBox="0 0 320 106" role="img" aria-label={label}>
            <g {...reveal}>
                {index !== 2 ? (
                    <path
                        className="trail"
                        d={
                            panVector
                                ? getPanArrow(panVector)
                                : depth
                                  ? 'M160 15V86 M155 21L160 15L165 21 M155 80L160 86L165 80'
                                  : 'M81 51H239 M87 46L81 51L87 56 M233 46L239 51L233 56'
                        }
                    />
                ) : null}
                {touch ? (
                    <>
                        {index === 0 ? (
                            <>
                                <circle
                                    cx={160 + x}
                                    cy={36 + y}
                                    r={10}
                                    className="contact"
                                />
                                <Finger x={160 + x} y={36 + y} />
                            </>
                        ) : null}
                        {index === 1 ? (
                            <>
                                <circle
                                    cx={153 + x}
                                    cy={35 + y}
                                    r={9}
                                    className="contact"
                                />
                                <circle
                                    cx={167 + x}
                                    cy={33 + y}
                                    r={9}
                                    className="contact"
                                />
                                <Finger x={160 + x} y={34 + y} fingers={2} />
                            </>
                        ) : null}
                        {index === 2 ? (
                            <Finger x={170} y={26} spread={(motion + 1) / 2} />
                        ) : null}
                    </>
                ) : (
                    <>
                        <g transform={`translate(${160 + x},${51 + y})`}>
                            <rect
                                x={-20}
                                y={-31}
                                width={40}
                                height={62}
                                rx={19}
                                className="device"
                            />
                            {index < 2 ? (
                                <path
                                    className="active"
                                    d={
                                        index === 0
                                            ? 'M-1 -29C-12 -29 -18 -21 -18 -11V-3H-1Z'
                                            : 'M1 -29C12 -29 18 -21 18 -11V-3H1Z'
                                    }
                                />
                            ) : null}
                            <path d="M0 -30V-4 M-19 -3H19" className="seam" />
                            <rect
                                x={-3.5}
                                y={-22}
                                width={7}
                                height={15}
                                rx={3.5}
                                className={
                                    index === 2 ? 'wheel wheel-active' : 'wheel'
                                }
                            />
                            {index === 2 ? (
                                <path
                                    d={`M-2 ${-16 + motion * 4}H2`}
                                    className="wheel-tick"
                                />
                            ) : null}
                        </g>
                        {index === 2 ? (
                            <>
                                <path
                                    className="trail"
                                    d="M198 25V76 M193 31L198 25L203 31 M193 70L198 76L203 70"
                                />
                                <circle
                                    cx={198}
                                    cy={51 - motion * 23}
                                    r={3}
                                    className="dot"
                                />
                            </>
                        ) : null}
                    </>
                )}
            </g>
        </Svg>
    );
};

const Svg = styled.svg`
    display: block;
    width: 100%;
    height: 106px;
    overflow: hidden;

    .device {
        fill: #fff;
        stroke: #60788e;
        stroke-width: 1.5;
    }
    .active {
        fill: ${({theme}) => theme.colors.secondary.main};
        fill-opacity: 0.2;
        stroke: ${({theme}) => theme.colors.secondary.main};
        stroke-width: 1.4;
    }
    .contact {
        fill: ${({theme}) => theme.colors.secondary.main};
        fill-opacity: 0.45;
        stroke: ${({theme}) => theme.colors.secondary.main};
        stroke-width: 1.5;
        stroke-opacity: 0.8;
    }
    .hand {
        fill: #fff;
        stroke: #60788e;
        stroke-width: 2.2;
        stroke-linejoin: round;
    }
    .hand-detail {
        fill: none;
        stroke: #60788e;
        stroke-width: 2.2;
        stroke-linecap: round;
    }
    .palm {
        fill: #fff;
    }
    .seam {
        fill: none;
        stroke: #60788e;
        stroke-width: 1;
    }
    .wheel {
        fill: #60788e;
    }
    .wheel-active,
    .dot {
        fill: ${({theme}) => theme.colors.secondary.main};
    }
    .wheel-tick {
        stroke: #fff;
        stroke-width: 2;
    }
    .note {
        fill: #60788e;
        font-size: 14px;
    }
    .trail {
        fill: none;
        stroke: ${({theme}) => theme.colors.secondary.main};
        stroke-width: 1.5;
        stroke-linecap: round;
        opacity: 0.7;
    }
`;
